import React from 'react'
import styled, { keyframes } from 'styled-components'
import AvatarItem from './AvatarItem'
import { IAvatarData } from '../../../helpers/data/avatars'
import { getAvatarName } from '../../../helpers/data/avatarNames'
import backgroundArrowImage from '../../../assets/views/characters/arrowBg.svg'

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
`

const Carroussel = styled.div`
	width: 76%;
	height: 100%;
	display: inline-block;
	vertical-align: top;
`

const Arrow = styled.button`
	width: 12%;
	height: 100%;
	background-color: transparent;
	border: none;
	cursor: pointer;
	display: inline-block;
	padding: 2% 0;
	vertical-align: top;

	&:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.85);
		outline-offset: 2px;
		border-radius: 12px;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}
	&.hidden {
		visibility: hidden;
	}
`

const ArrowImg = styled.img`
	width: 100%;
	height: 100%;
`

const ScreenReaderInstructions = styled.p`
	position: absolute;
	left: -9999px;
	width: 1px;
	height: 1px;
	overflow: hidden;
`

const ScreenReaderAnnouncement = styled.div`
	position: absolute;
	left: -9999px;
	width: 1px;
	height: 1px;
	overflow: hidden;
`

const appearAnim = keyframes`
	0% {
		opacity: 0;
		transform: scale(0.75, 0.75);
	}

	100% {
		opacity: 1;
		transform: scale(1, 1);
	}
`

const AvatarButton = styled.button`
	width: 25%;
	height: 100%;
	animation: ${appearAnim} 0.55s;
	animation-timing-function: cubic-bezier(0.435, 1, 0.74, 1);
	animation-fill-mode: forwards;
	display: inline-block;
	opacity: 0;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;

	&:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.85);
		outline-offset: 2px;
		border-radius: 16px;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&.teaser {
		width: 20%;
	}
`

const DotsContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 4px 0;
	gap: 8px;
`

const Dot = styled.button`
	width: 12px;
	height: 15px;
	border-radius: 100%;
	border: 2px solid rgba(255, 255, 255, 0.4);
	background-color: transparent;
	cursor: pointer;
	transition: all 0.3s ease;

	&:hover {
		border-color: rgba(255, 255, 255, 0.7);
	}

	&.active {
		background-color: rgba(255, 255, 255, 0.9);
		border-color: rgba(255, 255, 255, 0.9);
	}

	&:focus {
		outline: 2px solid rgba(255, 255, 255, 0.8);
		outline-offset: 2px;
	}
`

interface IAvatarsProps {
	avatars: IAvatarData[]
	onSelect: (id: string) => void
	selectedAvatarId: string
}

interface IAvatarsState {
	current: number
	screenReaderMessage: string
}

const AVATARS_PER_PAGE = 4

class Avatars extends React.PureComponent<IAvatarsProps, IAvatarsState> {
	state = {
		current: 0,
		screenReaderMessage: '',
	}

	componentDidMount() {
		this.setState({
			screenReaderMessage: this.getSRPagesUpdate(0),
		})
	}

	buildAvatars(avatars: IAvatarData[]) {
		const elements = avatars
			.filter((item, index) => {
				return index >= this.state.current * AVATARS_PER_PAGE && index < this.state.current * AVATARS_PER_PAGE + AVATARS_PER_PAGE
			})
			.map((item: IAvatarData, index: number) => {
				return (
					<AvatarButton
						key={item.id}
						className=''
						style={{
							animationDelay: `${0.05 * index}s`,
						}}
						type='button'
						onClick={() => this.props.onSelect(item.id)}
						aria-label={`${getAvatarName(item.id)}`}
						aria-current={item.id === this.props.selectedAvatarId ? 'true' : 'false'}
						role='button'
						tabIndex={0}
					>
						<AvatarItem
						id={item.id}
						icon={item.icon}
					/>
					</AvatarButton>
				)
			})

		return elements
	}

	navigate = (direction: number) => {
		const newCurrent = this.state.current + direction
		this.setState({
			current: newCurrent,
			screenReaderMessage: this.getSRPagesUpdate(newCurrent),
		})
	}

	navigateTo = (pageIndex: number) => {
		this.setState({
			current: pageIndex,
			screenReaderMessage: this.getSRPagesUpdate(pageIndex),
		})
	}

	navigateNext = () => this.navigate(1)
	navigateBack = () => this.navigate(-1)

	getSRPagesUpdate = (pageIndex: number): string => {
		const totalPages = Math.ceil(this.props.avatars.length / AVATARS_PER_PAGE)
		const currentPage = pageIndex + 1
		return `Page ${currentPage} of ${totalPages}`
	}

	buildDots() {
		const totalPages = Math.ceil(this.props.avatars.length / AVATARS_PER_PAGE)
		const dots = []

		for (let i = 0; i < totalPages; i++) {
			const isActive = i === this.state.current
			dots.push(
				<Dot
					key={i}
					className={isActive ? 'active' : ''}
					onClick={() => this.navigateTo(i)}
					aria-current={isActive ? 'true' : 'false'}
					aria-label={`Go to page ${i + 1} of ${totalPages}`}
					type="button"
				/>
			)
		}

		return dots
	}

	render() {
		const totalPages = Math.ceil(this.props.avatars.length / AVATARS_PER_PAGE)

		return (
			<Wrapper role='region' aria-label='Avatar selection carousel'>
				<ScreenReaderInstructions>
					Character selector. This slider contains {this.props.avatars.length} characters. Four characters are shown per page. Use the &quot;Go to next/previous page of avatars&quot; buttons to move between pages. Navigate through the characters and press Enter to select one.
				</ScreenReaderInstructions>
				<Arrow
					className={this.state.current === 0 ? 'hidden' : ''}
					onClick={this.navigateBack}
					type='button'
					aria-label={`Go to previous page of avatars`}
				>
					<ArrowImg src={backgroundArrowImage} alt="" />
				</Arrow>
				<Carroussel>{this.buildAvatars(this.props.avatars)}</Carroussel>
				<Arrow
					className={(this.state.current + 1) * AVATARS_PER_PAGE >= this.props.avatars.length ? 'hidden' : ''}
					onClick={this.navigateNext}
					type='button'
					aria-label={`Go to next page of avatars`}
				>
					<ArrowImg src={backgroundArrowImage} style={{ transform: 'scale(-1,1)' }} alt="" />
				</Arrow>
				{totalPages > 1 && (
					<DotsContainer>
						{this.buildDots()}
					</DotsContainer>
				)}
				<ScreenReaderAnnouncement
					role="status"
					aria-live="polite"
					aria-atomic="true"
				>
					{this.state.screenReaderMessage}
				</ScreenReaderAnnouncement>
			</Wrapper>
		)
	}
}

export default Avatars
